import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { Link } from 'react-router-dom';
import { fetchBidsForDepartment } from '../../services/alaCarteBidService';
import {
  PRICING_STATUS,
  BID_STATUS_LABELS,
  accountOverviewPath,
  bidAccountTypeLabel,
  isInternalBid,
} from '../../utils/alaCarteBid';
import { getTeamQueuePageTitle, teamAlaCartePaths } from '../../utils/teamAlaCarteRoutes';
import { Building2, Package } from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

const TAB_OPEN = 'open';
const TAB_SUBMITTED = 'submitted';
const TAB_WON = 'won';

function statusPillClass(status) {
  switch (status) {
    case PRICING_STATUS.published:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case PRICING_STATUS.awaiting_team:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
    case PRICING_STATUS.team_submitted:
    case PRICING_STATUS.exec_ready:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

/**
 * @param {{ department: 'Marketing'|'Finance'|'HR' }} props
 */
export default function TeamAlaCarteQueue({ department }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(TAB_OPEN);
  const [accountFilter, setAccountFilter] = useState('');
  const { queue: queuePath } = teamAlaCartePaths(department);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const bids = await fetchBidsForDepartment(department);
      setItems(bids);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [department]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const statusesByTab = {
      [TAB_OPEN]: [PRICING_STATUS.awaiting_team],
      [TAB_SUBMITTED]: [PRICING_STATUS.team_submitted, PRICING_STATUS.exec_ready],
      [TAB_WON]: [PRICING_STATUS.published],
    };
    const allow = statusesByTab[tab] || [];
    let list = items.filter((i) => allow.includes(i.pricingStatus));
    const q = accountFilter.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          (i.companyName || '').toLowerCase().includes(q) ||
          (i.name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, tab, accountFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
        tab === id
          ? 'bg-red-600 text-white shadow-md'
          : 'border border-gray-300 bg-white text-gray-700 hover:border-red-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-red-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen px-4 pb-10 pt-4 relative">
      <PortalBackground />
      <div className="relative mx-auto max-w-6xl">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {getTeamQueuePageTitle(department)}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Account-specific bids for {department} services
          </p>
        </header>

        <div className="mb-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <input
            type="search"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            placeholder="Filter by company or service…"
            className="w-full max-w-md rounded-full border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div className="mb-10 flex flex-wrap justify-center gap-3">
          {tabBtn(TAB_OPEN, 'Open')}
          {tabBtn(TAB_SUBMITTED, 'Submitted')}
          {tabBtn(TAB_WON, 'Won')}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white/80 p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900/80">
            <Package className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400">No bids in this list.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {filtered.map((item) => {
              const accountPath = accountOverviewPath(item);
              return (
                <li key={item.id}>
                  <Link
                    to={`${queuePath}/${item.id}`}
                    className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-red-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-red-800"
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <span className="text-3xl leading-none">{item.icon || '📦'}</span>
                      <span
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusPillClass(
                          item.pricingStatus
                        )}`}
                      >
                        {BID_STATUS_LABELS[item.pricingStatus] || item.pricingStatus}
                      </span>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{item.name}</h2>
                    {(accountPath || isInternalBid(item)) && (
                      <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{item.companyName || 'Unknown company'}</span>
                        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {bidAccountTypeLabel(item.accountType)}
                        </span>
                      </p>
                    )}
                    <p className="mt-2 line-clamp-2 flex-1 text-sm text-gray-500 dark:text-gray-400">
                      {item.description || '—'}
                    </p>
                    <span className="mt-4 text-xs font-medium text-red-600 dark:text-red-400">View & edit →</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
